import { sortEvents } from "./dateUtils";

/**
 * Build a git-tree style layout for timeline events.
 *
 * @param {Array} events - Array of event objects
 * @returns {Object} Layout structure with positions, branches, and connectors
 */
export function buildTimelineLayout(events) {
  if (!events.length) return { treeNodes: [], connectors: [], branchLines: [], minDate: null, maxDate: null, laneCount: 0 };

  const sorted = sortEvents(events);

  // Get date range
  const dates = sorted.map((e) => e.startDate);
  const rangeEnds = sorted.filter((e) => e.type === "range" && e.endDate).map((e) => e.endDate);
  const allDates = [...dates, ...rangeEnds].sort();
  const minDate = allDates[0];
  const maxDate = allDates[allDates.length - 1] || allDates[0];

  // Assign lanes (branches) to events using greedy algorithm
  // Each event gets a lane. Range events occupy a lane from start to end.
  // Bifurcations (parentId) branch off from the parent's lane.
  const lanes = []; // array of { eventId, endDate }
  const eventLane = {}; // eventId -> laneIndex
  const branchColors = {}; // laneIndex -> color (inherits from first event on that lane)

  for (const event of sorted) {
    const endDate = event.type === "range" && event.endDate ? event.endDate : event.startDate;

    // If this event has a parent, try to place it adjacent to the parent's lane
    if (event.parentId && eventLane[event.parentId] !== undefined) {
      const parentLane = eventLane[event.parentId];
      // Find a free lane near the parent
      let foundLane = false;
      for (let offset = 0; offset < Math.max(lanes.length + 1, 5); offset++) {
        const tryLane = parentLane + offset;
        const laneFree = !lanes[tryLane] || lanes[tryLane].endDate <= event.startDate;
        if (laneFree && tryLane >= 0) {
          lanes[tryLane] = { eventId: event.id, endDate };
          eventLane[event.id] = tryLane;
          if (!branchColors[tryLane] && event.color) {
            branchColors[tryLane] = event.color;
          }
          foundLane = true;
          break;
        }
      }
      if (!foundLane) {
        // Fallback: assign to a new lane
        const idx = lanes.length;
        lanes.push({ eventId: event.id, endDate });
        eventLane[event.id] = idx;
        if (event.color) branchColors[idx] = event.color;
      }
    } else {
      // Top-level event: find first free lane
      let assignedLane = -1;
      for (let i = 0; i < lanes.length; i++) {
        if (!lanes[i] || lanes[i].endDate < event.startDate) {
          assignedLane = i;
          break;
        }
      }
      if (assignedLane === -1) {
        assignedLane = lanes.length;
        lanes.push(null);
      }
      lanes[assignedLane] = { eventId: event.id, endDate };
      eventLane[event.id] = assignedLane;
      if (event.color) branchColors[assignedLane] = event.color;
    }
  }

  const laneCount = lanes.length;

  // Build tree nodes: one for each event start, plus end nodes for range events
  const treeNodes = [];
  const eventNodes = []; // per event, list of node indices

  for (const event of sorted) {
    const lane = eventLane[event.id] ?? 0;
    const color = event.color || branchColors[lane] || getDefaultColor(lane);

    // Start node
    const startNode = {
      id: `node-start-${event.id}`,
      eventId: event.id,
      type: event.type === "range" ? "range-start" : "punctual",
      date: event.startDate,
      lane,
      color,
      title: event.title,
      textSize: event.textSize,
      description: event.description,
      image: event.image,
    };
    treeNodes.push(startNode);
    const nodes = [treeNodes.length - 1];

    // End node for range events
    if (event.type === "range" && event.endDate && event.endDate !== event.startDate) {
      const endNode = {
        id: `node-end-${event.id}`,
        eventId: event.id,
        type: "range-end",
        date: event.endDate,
        lane,
        color,
        title: event.title,
        textSize: event.textSize,
        description: event.description,
        image: event.image,
      };
      treeNodes.push(endNode);
      nodes.push(treeNodes.length - 1);
    }

    eventNodes.push({ event, nodes, lane, color });
  }

  // Build branch lines: continuous vertical lines for each lane
  const branchLines = [];
  for (let i = 0; i < laneCount; i++) {
    // Find the min and max date for this lane
    const laneEvents = sorted.filter((e) => eventLane[e.id] === i);
    if (laneEvents.length === 0) continue;

    const laneStartDate = laneEvents.reduce((min, e) => (e.startDate < min ? e.startDate : min), laneEvents[0].startDate);
    const laneEndDate = laneEvents.reduce((max, e) => {
      const end = e.type === "range" && e.endDate ? e.endDate : e.startDate;
      return end > max ? end : max;
    }, laneStartDate);

    branchLines.push({
      lane: i,
      startDate: laneStartDate,
      endDate: laneEndDate,
      color: branchColors[i] || getDefaultColor(i),
      eventIds: laneEvents.map((e) => e.id),
    });
  }

  // Build connectors: for range events, connect start node to end node
  // For bifurcations, connect parent event to child event
  const connectors = [];
  const mainTrunkLane = 0;

  // Range connectors
  for (const { event, nodes } of eventNodes) {
    if (event.type === "range" && nodes.length === 2) {
      connectors.push({
        type: "range",
        eventId: event.id,
        fromNode: nodes[0],
        toNode: nodes[1],
        fromIdx: nodes[0],
        toIdx: nodes[1],
        color: event.color || branchColors[eventLane[event.id]] || getDefaultColor(eventLane[event.id]),
      });
    }
  }

  // Branch jump connectors: when an event's parent is on a different lane,
  // draw a horizontal connector from the parent's lane to the child's lane
  for (const event of sorted) {
    if (event.parentId && eventLane[event.id] !== undefined && eventLane[event.parentId] !== undefined) {
      const parentLane = eventLane[event.parentId];
      const childLane = eventLane[event.id];
      if (parentLane !== childLane && treeNodes.some((n) => n.eventId === event.parentId)) {
        connectors.push({
          type: "bifurcation",
          eventId: event.id,
          parentId: event.parentId,
          parentLane,
          childLane,
          date: event.startDate,
          color: event.color || getDefaultColor(childLane),
        });
      }
    }
  }

  return { treeNodes, connectors, branchLines, eventNodes, minDate, maxDate, laneCount };
}

function getDefaultColor(index) {
  const colors = ["#4A90D9", "#7B61FF", "#E05D5D", "#3CB371", "#FF8C42", "#6C5CE7", "#00B4D8", "#E76F51", "#2A9D8F", "#F4A261"];
  return colors[index % colors.length];
}

/**
 * Calculate the vertical position for a date within the timeline range.
 */
export function dateToY(dateStr, minDate, maxDate, totalHeight = 2000) {
  if (!minDate || !maxDate) return 0;
  const date = new Date(dateStr);
  const min = new Date(minDate);
  const max = new Date(maxDate);
  const range = max.getTime() - min.getTime();
  if (range === 0) return totalHeight / 2;
  return ((date.getTime() - min.getTime()) / range) * totalHeight;
}

/**
 * Generate date tick markers with density adapted to available axis length.
 * @param {string} minDate
 * @param {string} maxDate
 * @param {number} [axisLengthPx=800] - usable pixel length of the time axis
 * @returns {{ date: string, kind: 'year'|'quarter'|'month' }[]}
 */
export function generateDateTicks(minDate, maxDate, axisLengthPx = 800) {
  if (!minDate || !maxDate) return [];

  const min = new Date(minDate);
  const max = new Date(maxDate);
  const rangeMs = Math.max(max.getTime() - min.getTime(), 1);
  const rangeDays = rangeMs / (1000 * 60 * 60 * 24);
  const pxPerDay = axisLengthPx / rangeDays;
  const pxPerMonth = pxPerDay * 30.44;
  const pxPerYear = pxPerDay * 365.25;

  // Pick granularity so labels stay readable (~48px min gap)
  let stepMonths = 12; // yearly
  let kind = "year";
  if (pxPerMonth >= 48) {
    stepMonths = 1;
    kind = "month";
  } else if (pxPerMonth * 3 >= 48) {
    stepMonths = 3;
    kind = "quarter";
  } else if (pxPerYear < 40) {
    // Very compressed: skip years until spacing is ok
    stepMonths = 12 * Math.max(1, Math.ceil(48 / pxPerYear));
    kind = "year";
  }

  const ticks = [];
  const current = new Date(min.getFullYear(), 0, 1);
  const end = new Date(max.getFullYear() + 1, 0, 1);

  while (current <= end) {
    const month = current.getMonth();
    const isYearStart = month === 0;
    const include =
      stepMonths >= 12
        ? isYearStart && current.getFullYear() % Math.round(stepMonths / 12) === 0
        : month % stepMonths === 0;

    if (include) {
      ticks.push({
        date: formatTickDate(current),
        kind: isYearStart ? "year" : kind,
      });
    }
    current.setMonth(current.getMonth() + 1);
  }

  return ticks;
}

function formatTickDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export { sortEvents };
